import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiErr } from "@/lib/apiClient";
import { toast } from "sonner";
import { Search, TrendingUp, Zap, ExternalLink, CheckCircle2, Loader2, SlidersHorizontal, Award, Code2, Rocket, FlaskConical, Briefcase } from "lucide-react";

const TYPE_ICON = { Certification: Award, Project: Code2, Hackathon: Rocket, Research: FlaskConical, Internship: Briefcase };

export default function Opportunities() {
  const { refreshUser } = useAuth();
  const [ops, setOps] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [career, setCareer] = useState("");
  const [type, setType] = useState("");
  const [minImpact, setMinImpact] = useState(0);
  const [search, setSearch] = useState("");
  const [completingId, setCompletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/opportunities", {
      params: { career_id: career || undefined, action_type: type || undefined, min_impact: minImpact || undefined, search: search || undefined, limit: 60 },
    });
    setOps(data.opportunities);
    setLoading(false);
  };

  useEffect(() => { api.get("/tracks").then(({ data }) => setTracks(data.tracks)); }, []);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [career, type, minImpact, search]);

  const complete = async (op) => {
    setCompletingId(op.id);
    try {
      const { data } = await api.post(`/actions/${op.id}/complete`);
      toast.success(`+${data.xp_earned} XP · Streak ${data.current_streak} 🔥`);
      await refreshUser();
      await load();
    } catch (e) { toast.error(apiErr(e)); } finally { setCompletingId(null); }
  };

  return (
    <div className="max-w-6xl">
      <p className="eyebrow text-slate-400">CV IMPACT & OPPORTUNITY ENGINE</p>
      <h1 className="font-display font-extrabold text-3xl tracking-tight text-white mt-1 mb-6">Actions that move your readiness</h1>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-5 border border-slate-200 mb-6">
        <div className="flex items-center gap-2 text-slate-500 eyebrow mb-3"><SlidersHorizontal size={14} /> FILTERS</div>
        <div className="grid md:grid-cols-4 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-slate-400" />
            <input data-testid="opp-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actions..."
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-sm focus:border-[#6366F1] outline-none transition-colors" />
          </div>
          <select data-testid="opp-career-filter" value={career} onChange={(e) => setCareer(e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm">
            <option value="">All my tracks</option>
            {tracks.map((t) => <option key={t.career_id} value={t.career_id}>{t.career_name}</option>)}
          </select>
          <select data-testid="opp-type-filter" value={type} onChange={(e) => setType(e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm">
            <option value="">All types</option>
            {["Certification", "Project", "Hackathon", "Research", "Internship"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 whitespace-nowrap font-mono">min +{minImpact}%</span>
            <input data-testid="opp-impact-slider" type="range" min="0" max="20" step="1" value={minImpact}
              onChange={(e) => setMinImpact(Number(e.target.value))} className="w-full accent-[#6366F1]" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>
      ) : ops.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 border border-dashed border-slate-300 text-center">
          <p className="text-slate-500">No opportunities match. Try lowering the impact filter or adding career tracks.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {ops.map((op) => {
            const Icon = TYPE_ICON[op.Action_Type] || Award;
            return (
              <div key={op.id} data-testid={`opp-card-${op.id}`} className="bg-white rounded-xl p-6 border border-slate-200 hover:-translate-y-1 transition-transform flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                    <Icon size={13} /> {op.Action_Type}
                  </span>
                  <span className="text-[11px] font-mono text-slate-400">by {op.Deadline_Date}</span>
                </div>
                <h3 className="font-bold text-slate-900 leading-snug">{op.Title}</h3>
                <p className="text-xs text-slate-500 mt-2 line-clamp-2 flex-1">{op.Description}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="inline-flex items-center gap-1 text-xs font-mono font-bold px-2.5 py-1 rounded-full bg-[#22C55E]/10 text-[#16A34A] border border-[#22C55E]/30">
                    <TrendingUp size={13} /> +{op.Estimated_Impact_Pct}%
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-mono font-bold px-2.5 py-1 rounded-full bg-[#6366F1]/10 text-[#4F46E5] border border-[#6366F1]/30">
                    <Zap size={13} /> +{op.XP_Reward} XP
                  </span>
                </div>
                <div className="flex gap-2 mt-4">
                  {op.completed ? (
                    <span className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-green-600 bg-green-50 py-2.5 rounded-lg border border-green-200">
                      <CheckCircle2 size={16} /> Completed
                    </span>
                  ) : (
                    <button data-testid={`opp-complete-${op.id}`} onClick={() => complete(op)} disabled={completingId === op.id}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white text-sm font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-60">
                      {completingId === op.id ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Complete
                    </button>
                  )}
                  <a href={op.External_Link} target="_blank" rel="noreferrer" data-testid={`opp-link-${op.id}`}
                    className="inline-flex items-center justify-center px-3 py-2.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 transition-colors">
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
